import { isNotNullish, isNullish } from '@nx/stdlib';
import {
  type FlatSavedCypherItem,
  type FlatTreeItems,
  type TreeItems,
  isSavedCypherFolder,
  isSavedCypherQuery,
} from '@query/types/saved-cypher';
import { ROOT_ITEM_ID, buildTree } from '@query/utils/saved-cypher-utils';
import { cloneDeep } from 'lodash-es';

import type { Item, ItemDocument, OrderedItem, OrderedItems, PartialItem, SavedCypherTree } from './documents';

const buildSavedCypherTreeOrderedItemsFromCorrelatedItems = (
  items: PartialItem[],
  itemDocs: ItemDocument[],
): OrderedItems => {
  // Map clientId -> API generated item document
  // Used to lookup an item's parent document by the parent's clientId
  const itemDocByClientIdMap = new Map<string, ItemDocument>();
  for (const itemDoc of itemDocs) {
    if (isNotNullish(itemDoc.clientId)) {
      itemDocByClientIdMap.set(itemDoc.clientId, itemDoc);
    }
  }

  // Map clientId -> application generated item
  // Used to lookup an application generated item to find its parent's clientId
  const itemByClientIdMap = new Map<string, PartialItem>();
  for (const item of items) {
    if (isNotNullish(item.clientId)) {
      itemByClientIdMap.set(item.clientId, item);
    }
  }

  // Map API generated id -> OrderedItem
  // Used to keep track of ordered items and their children when building the ordered items structure
  const orderedItemByItemDocIdMap = new Map<string, OrderedItem>();
  for (const itemDoc of itemDocs) {
    orderedItemByItemDocIdMap.set(itemDoc.id, { id: itemDoc.id, parentId: ROOT_ITEM_ID, children: [] });
  }

  // Lookup the parentId for an item using its clientId.
  const findItemParentId = (item?: PartialItem): string => {
    if (isNullish(item) || isNullish(item.parentId)) {
      return ROOT_ITEM_ID;
    }

    if (itemDocByClientIdMap.has(item.parentId)) {
      // If the item's parentId exists in both the itemByClientIdMap and the itemDocByClientIdMap,
      // it is a new item that was created in the same batch.
      // So we need to find the API generated id for this parent.
      return itemDocByClientIdMap.get(item.parentId)?.id ?? ROOT_ITEM_ID;
    }

    // Otherwise, the parent is pre-existing, and we can use the parentId from it directly.
    return item.parentId;
  };

  const orderedItems: OrderedItems = [];
  for (const itemDoc of itemDocs) {
    const item = isNotNullish(itemDoc.clientId) ? itemByClientIdMap.get(itemDoc.clientId) : undefined;
    const parentId = findItemParentId(item);

    const orderedItem = orderedItemByItemDocIdMap.get(itemDoc.id);
    if (isNullish(orderedItem)) {
      continue;
    }

    orderedItem.parentId = parentId;

    if (isNotNullish(parentId) && orderedItemByItemDocIdMap.has(parentId)) {
      // If this orderedItem has a parent, and the parent exists in the orderedItemByItemDocIdMap,
      // add this ordered item as a child
      const parentOrderedItem = orderedItemByItemDocIdMap.get(parentId);
      if (isNotNullish(parentOrderedItem)) {
        parentOrderedItem.children = parentOrderedItem.children ?? [];
        parentOrderedItem.children.push(orderedItem);
      }
    } else {
      // Otherwise, add it to the root
      orderedItems.push(orderedItem);
    }
  }

  return orderedItems;
};

export const mergeOrderedItems = (
  existingOrderedItems: OrderedItems | null,
  newItems: PartialItem[],
  createdItems: ItemDocument[],
): OrderedItems => {
  const newOrderedItems = buildSavedCypherTreeOrderedItemsFromCorrelatedItems(newItems, createdItems);

  if (isNullish(existingOrderedItems)) {
    return newOrderedItems;
  }

  // Remove parentId and children properties from the OrderedItem.
  const cleanOrderedItem = ({ parentId, children, ...item }: OrderedItem): OrderedItem => {
    return {
      ...item,
      ...(children && children.length >= 1 ? { children: children.map(cleanOrderedItem) } : {}),
    };
  };

  // Map parentId -> orderedItems
  // Used to lookup an orderedItem's children by its parentId
  const orderedItemsByParentIdMap = newOrderedItems.reduce<Map<string | null, OrderedItems>>((acc, item) => {
    const parentIdKey = item.parentId ?? ROOT_ITEM_ID;
    acc.set(parentIdKey, [...(acc.get(parentIdKey) ?? []), cleanOrderedItem(item)]);
    return acc;
  }, new Map());

  // Merge existing ordered items with new ordered items
  const recursivelyMergeOrderedItems = (orderedItems: OrderedItems): OrderedItems => {
    return orderedItems.map((orderedItem) => {
      // Recursively merge children, if they exist
      const children = orderedItem.children ? recursivelyMergeOrderedItems(orderedItem.children) : [];
      // Find the new children for this ordered item based on its id
      const orderedItemChildren = orderedItemsByParentIdMap.get(orderedItem.id) ?? [];
      const newChildren = orderedItemChildren.map((child) => {
        return child.children
          ? {
              ...child,
              children: recursivelyMergeOrderedItems(child.children),
            }
          : {
              ...child,
            };
      });

      return {
        ...orderedItem,
        ...(children.length || newChildren.length ? { children: [...children, ...newChildren] } : null),
      };
    });
  };

  const mergedOrderedItems = recursivelyMergeOrderedItems(cloneDeep(existingOrderedItems));
  const newRootOrderedItems = orderedItemsByParentIdMap.get(ROOT_ITEM_ID) ?? [];
  return [...mergedOrderedItems, ...newRootOrderedItems];
};

export const removeItemIdsFromSavedCypherTree = (
  savedCypherTree: SavedCypherTree,
  itemIds: string[] | string,
): SavedCypherTree => {
  const idSet = new Set(Array.isArray(itemIds) ? itemIds : [itemIds]);

  const pruneOrderedItems = (orderedItems: OrderedItems): OrderedItems => {
    return orderedItems
      .filter((orderedItem) => !idSet.has(orderedItem.id))
      .map((orderedItem) => {
        if (isNotNullish(orderedItem.children)) {
          const children = pruneOrderedItems(orderedItem.children);
          return children.length > 0 ? { ...orderedItem, children } : { ...orderedItem, children: undefined };
        }
        return { ...orderedItem };
      });
  };

  return {
    ...savedCypherTree,
    orderedItems: pruneOrderedItems([...(savedCypherTree.orderedItems ?? [])]),
  };
};

export const buildFlatTreeItemsFromCorrelatedItems = (orderedItems: OrderedItems, items: Item[]): FlatTreeItems => {
  const buildFlatTreeItemMapFromOrderedItems = (
    orderedItemsToFlatten: OrderedItems,
    parentId?: string,
    depth = 0,
  ): Map<string, FlatSavedCypherItem> => {
    const map = new Map<string, FlatSavedCypherItem>();

    for (const [order, orderedItem] of orderedItemsToFlatten.entries()) {
      map.set(orderedItem.id, {
        id: orderedItem.id,
        name: '',
        parentId: parentId ?? ROOT_ITEM_ID,
        children: [],
        order,
        depth,
      });

      if (orderedItem.children) {
        const childMap = buildFlatTreeItemMapFromOrderedItems(orderedItem.children, orderedItem.id, depth + 1);
        for (const [childOrderedItemId, childOrderedItem] of childMap.entries()) {
          map.set(childOrderedItemId, childOrderedItem);
        }
      }
    }

    return map;
  };

  const flatTreeItemsMap = buildFlatTreeItemMapFromOrderedItems(orderedItems);

  for (const [order, item] of items.entries()) {
    const orderedItem = flatTreeItemsMap.get(item.id);

    const baseFlatItem = {
      id: item.id,
      name: item.name,
      parentId: orderedItem?.parentId ?? ROOT_ITEM_ID,
      depth: orderedItem?.depth ?? 0,
      order: orderedItem?.order ?? order,
    };

    const mapToFlatItem = (itemToMap: Item, parent?: FlatSavedCypherItem): FlatSavedCypherItem => {
      if (isSavedCypherQuery(itemToMap)) {
        return {
          ...baseFlatItem,
          parentId: parent ? parent.id : ROOT_ITEM_ID,
          depth: parent ? parent.depth + 1 : 0,
          query: itemToMap.query,
        };
      }

      return {
        ...baseFlatItem,
        parentId: parent ? parent.id : ROOT_ITEM_ID,
        depth: parent ? parent.depth + 1 : 0,
        children: [],
      };
    };

    const parent = flatTreeItemsMap.get(orderedItem?.parentId ?? ROOT_ITEM_ID) ?? undefined;
    flatTreeItemsMap.set(item.id, mapToFlatItem(item, parent));
  }

  return Array.from(flatTreeItemsMap.values());
};

export const buildSavedCypherTreeOrderedItemsFromFlatTreeItems = (flatItems: FlatTreeItems): OrderedItems => {
  const buildSavedCypherTreeOrderedItemsInternal = (items: TreeItems): OrderedItems => {
    return items.map((item) => {
      if (isSavedCypherFolder(item)) {
        return {
          id: item.id,
          children: buildSavedCypherTreeOrderedItemsInternal(item.children),
        };
      }
      return {
        id: item.id,
      };
    });
  };

  const tree = buildTree(flatItems);
  return buildSavedCypherTreeOrderedItemsInternal(tree);
};
